import { Button, IconButton, Input, Switch, Textarea } from '@artosera/ui';
import { Plus, Trash2 } from 'lucide-react';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { estApiError } from '../../../api/errors.js';
import {
  RUBRIQUES_PAGE,
  useModifierPage,
  usePages,
  type MembreEquipeSite,
  type PageSite,
  type RubriquePage,
} from '../../../api/site.js';
import { galerieALaction } from '../../../auth/sujet.js';
import type { GalerieSession } from '../../../api/types.js';
import { Field } from '../../Field.js';
import styles from './Tabs.module.css';

/** Initiales d'un membre (« Claire Vallier » → « CV »), pour son monogramme. */
function monogramme(nom: string): string {
  return nom
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((mot) => mot.charAt(0).toLocaleUpperCase('fr'))
    .join('');
}

export interface PagesTabProps {
  galerieSlug: string;
  galerie: GalerieSession;
}

/**
 * Pages fixes (S6-f, § 15.8) : La galerie, Contact, Mentions légales,
 * Confidentialité. Édition FR/EN ; `contenu` est du texte en paragraphes
 * (une ligne vide sépare deux paragraphes, `paragraphes()` du moteur), pas du
 * Markdown — un simple textarea suffit donc, sans éditeur enrichi.
 */
export function PagesTab({ galerieSlug, galerie }: PagesTabProps): JSX.Element {
  const { t } = useTranslation(['regie', 'site', 'commun']);
  const pages = usePages(galerieSlug);
  const [rubrique, setRubrique] = useState<RubriquePage>('la-galerie');

  if (pages.isLoading) return <p className={styles.vide}>{t('commun:chargement')}</p>;
  if (pages.isError || !pages.data) {
    return (
      <p className={styles.erreur} role="alert">
        {estApiError(pages.error) ? pages.error.message : t('commun:erreurReseau')}
      </p>
    );
  }

  const page = pages.data.find((valeur) => valeur.rubrique === rubrique);

  return (
    <div className={styles.formulaire}>
      <div className={styles.pageOnglets} role="tablist" aria-label={t('regie:site.onglets.pages')}>
        {RUBRIQUES_PAGE.map((code) => (
          <button
            key={code}
            type="button"
            role="tab"
            aria-selected={rubrique === code}
            className={styles.pageOnglet}
            data-actif={rubrique === code ? 'true' : 'false'}
            onClick={() => setRubrique(code)}
          >
            {t(`site:pages.${code === 'la-galerie' ? 'galerie' : code}`)}
          </button>
        ))}
      </div>

      {page ? (
        <PageFormulaire
          key={page.id}
          galerieSlug={galerieSlug}
          page={page}
          peutEcrire={galerieALaction(galerie, 'pages:write')}
        />
      ) : null}
    </div>
  );
}

function PageFormulaire({
  galerieSlug,
  page,
  peutEcrire,
}: {
  galerieSlug: string;
  page: PageSite;
  peutEcrire: boolean;
}): JSX.Element {
  const { t } = useTranslation(['regie', 'site', 'commun']);
  const modifier = useModifierPage(galerieSlug);

  const [titreFr, setTitreFr] = useState(page.titre.fr);
  const [titreEn, setTitreEn] = useState(page.titre.en ?? '');
  const [contenuFr, setContenuFr] = useState(page.contenu.fr);
  const [contenuEn, setContenuEn] = useState(page.contenu.en ?? '');
  const [equipe, setEquipe] = useState<MembreEquipeSite[]>([...page.equipe]);
  const [appelFr, setAppelFr] = useState(page.appelNewsletter?.fr ?? '');
  const [appelEn, setAppelEn] = useState(page.appelNewsletter?.en ?? '');
  const [visibleSurLeSite, setVisibleSurLeSite] = useState(page.visibleSurLeSite);
  const [erreur, setErreur] = useState<string | null>(null);
  const [succes, setSucces] = useState(false);

  async function enregistrer(): Promise<void> {
    if (!page.rubrique) return;
    setErreur(null);
    setSucces(false);
    try {
      await modifier.mutateAsync({
        rubrique: page.rubrique,
        corps: {
          titre: { fr: titreFr, ...(titreEn ? { en: titreEn } : {}) },
          contenu: { fr: contenuFr, ...(contenuEn ? { en: contenuEn } : {}) },
          ...(page.rubrique === 'la-galerie' ? { equipe } : {}),
          ...(page.rubrique === 'contact'
            ? {
                appelNewsletter: appelFr
                  ? { fr: appelFr, ...(appelEn ? { en: appelEn } : {}) }
                  : null,
              }
            : {}),
          visibleSurLeSite,
        },
      });
      setSucces(true);
    } catch (error) {
      setErreur(estApiError(error) ? error.message : t('commun:erreurReseau'));
    }
  }

  return (
    <form className={styles.formulaire} onSubmit={(event) => event.preventDefault()} noValidate>
      <div className={`${styles.panneau} ${styles.panneauPlein}`}>
        <div className={styles.grille2}>
          <div className={styles.feuille}>
            <Field label={t('regie:site.pages.titreFr')} htmlFor="page-titre-fr">
              <Input
                id="page-titre-fr"
                disabled={!peutEcrire}
                value={titreFr}
                onChange={(event) => setTitreFr(event.target.value)}
              />
            </Field>
            <Field label={t('regie:site.pages.contenuFr')} htmlFor="page-contenu-fr">
              <Textarea
                id="page-contenu-fr"
                rows={10}
                disabled={!peutEcrire}
                value={contenuFr}
                onChange={(event) => setContenuFr(event.target.value)}
              />
            </Field>
          </div>
          <div className={styles.feuille}>
            <Field label={t('regie:site.pages.titreEn')} htmlFor="page-titre-en">
              <Input
                id="page-titre-en"
                disabled={!peutEcrire}
                value={titreEn}
                onChange={(event) => setTitreEn(event.target.value)}
              />
            </Field>
            <Field label={t('regie:site.pages.contenuEn')} htmlFor="page-contenu-en">
              <Textarea
                id="page-contenu-en"
                rows={10}
                disabled={!peutEcrire}
                value={contenuEn}
                onChange={(event) => setContenuEn(event.target.value)}
              />
            </Field>
          </div>
        </div>
        <p className={styles.aide}>{t('regie:site.pages.paragraphesAide')}</p>
      </div>

      {page.rubrique === 'la-galerie' ? (
        <section className={styles.panneau} aria-labelledby="page-equipe-titre">
          <div className={styles.panneauEntete}>
            <h2 id="page-equipe-titre" className={styles.sectionTitre}>
              {t('regie:site.pages.equipeTitre')}
            </h2>
          </div>
          <div className={styles.panneauCorps}>
            <ul className={styles.membreListe}>
              {equipe.map((membre, index) => (
                <li key={String(index)} className={styles.membreLigne}>
                  <span className={styles.monogramme} aria-hidden="true">
                    {monogramme(membre.nom)}
                  </span>
                  <Input
                    aria-label={t('regie:site.pages.membreNom')}
                    placeholder={t('regie:site.pages.membreNom')}
                    disabled={!peutEcrire}
                    value={membre.nom}
                    onChange={(event) =>
                      setEquipe((precedent) =>
                        precedent.map((valeur, i) =>
                          i === index ? { ...valeur, nom: event.target.value } : valeur,
                        ),
                      )
                    }
                  />
                  <Input
                    aria-label={t('regie:site.pages.membreRoleFr')}
                    placeholder={t('regie:site.pages.membreRoleFr')}
                    disabled={!peutEcrire}
                    value={membre.role.fr}
                    onChange={(event) =>
                      setEquipe((precedent) =>
                        precedent.map((valeur, i) =>
                          i === index
                            ? { ...valeur, role: { ...valeur.role, fr: event.target.value } }
                            : valeur,
                        ),
                      )
                    }
                  />
                  <Input
                    aria-label={t('regie:site.pages.membreEmail')}
                    placeholder={t('regie:site.pages.membreEmail')}
                    disabled={!peutEcrire}
                    value={membre.email ?? ''}
                    onChange={(event) =>
                      setEquipe((precedent) =>
                        precedent.map((valeur, i) =>
                          i === index
                            ? { ...valeur, email: event.target.value || undefined }
                            : valeur,
                        ),
                      )
                    }
                  />
                  {peutEcrire ? (
                    <IconButton
                      variant="outline"
                      icon={<Trash2 aria-hidden />}
                      aria-label={t('commun:actions.supprimer')}
                      onClick={() =>
                        setEquipe((precedent) => precedent.filter((_, i) => i !== index))
                      }
                    />
                  ) : null}
                </li>
              ))}
            </ul>
            {peutEcrire ? (
              <Button
                variant="secondary"
                className={styles.ajoutLigne}
                onClick={() =>
                  setEquipe((precedent) => [...precedent, { nom: '', role: { fr: '' } }])
                }
              >
                <Plus size={16} strokeWidth={1.5} aria-hidden />
                {t('regie:site.pages.ajouterMembre')}
              </Button>
            ) : null}
          </div>
        </section>
      ) : null}

      {page.rubrique === 'contact' ? (
        <div className={`${styles.panneau} ${styles.panneauPlein}`}>
          <div className={styles.grille2}>
            <Field label={t('regie:site.pages.appelNewsletterFr')} htmlFor="page-appel-fr">
              <Input
                id="page-appel-fr"
                disabled={!peutEcrire}
                value={appelFr}
                onChange={(event) => setAppelFr(event.target.value)}
              />
            </Field>
            <Field label={t('regie:site.pages.appelNewsletterEn')} htmlFor="page-appel-en">
              <Input
                id="page-appel-en"
                disabled={!peutEcrire}
                value={appelEn}
                onChange={(event) => setAppelEn(event.target.value)}
              />
            </Field>
          </div>
        </div>
      ) : null}

      <div className={styles.barreActions}>
        <div className={styles.barreVisibilite}>
          <Switch
            id="page-visible"
            label={t('regie:site.pages.visibleSurLeSite')}
            checked={visibleSurLeSite}
            disabled={!peutEcrire}
            onCheckedChange={setVisibleSurLeSite}
          />
        </div>
        <div className={styles.barreMessages}>
          {erreur ? (
            <p className={styles.erreur} role="alert">
              {erreur}
            </p>
          ) : null}
          {succes ? (
            <p className={styles.succes} role="status">
              {t('site:messages.enregistre')}
            </p>
          ) : null}
        </div>
        {peutEcrire ? (
          <Button onClick={() => void enregistrer()} loading={modifier.isPending}>
            {t('commun:actions.enregistrer')}
          </Button>
        ) : null}
      </div>
    </form>
  );
}
